import { SettingsContainer } from "@/components/ui/SettingsContainer";
import { Button } from "@/components/ui/button/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import useUpdateGuestBusinessDetails from "@/features/guest-business-details/useUpdateGuestBusinessDetails";
import { authenticatedGuestRoute, businessDetailsSettingRoute } from "@/router";
import {
	useLoaderData,
	useNavigate,
	useParams,
	useRouter,
} from "@tanstack/react-router";
import { ArrowLeftIcon, X } from "lucide-react";
import { BusinessDetailsForm } from "./BusinessDetailsForm";
import { recordToFormValues } from "./BusinessDetailsForm.utils";

const BusinessDetailsEditPage = () => {
	const navigate = useNavigate();
	const router = useRouter();
	const { toast } = useToast();
	const { user } = useAuth();
	const { location_id } = useLoaderData({ from: "__root__" });
	const { states } = useLoaderData({ from: "/main/setting/business-details" });
	const { businessDetailId } = useParams({
		from: "/main/setting/business-details/edit/$businessDetailId",
	});
	const detailId = Number(businessDetailId);

	const records = user.guest_business_details ?? [];
	const record = records.find((r) => r.id === detailId);
	const mutation = useUpdateGuestBusinessDetails({
		onSuccess: async () => {
			toast({
				title: "Business details saved",
				description: "Your business information has been updated.",
			});
			await router.invalidate({
				filter: ({ routeId }) =>
					routeId === authenticatedGuestRoute.id ||
					routeId === businessDetailsSettingRoute.id,
				sync: true,
			});
			navigate({ to: "/main/setting/business-details" });
		},
		onError: (error) => {
			toast({
				title: "Could not save",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	return (
		<SettingsContainer className="flex flex-col max-w-screen-sm bg-white text-textDark pb-10">
			<div className="mb-4 flex justify-end md:justify-start">
				<Button
					variant="ghost"
					size="compact"
					className="self-start"
					type="button"
					onClick={() => navigate({ to: "/main/setting/business-details" })}
				>
					<X className="w-6 h-6 text-textDark md:hidden" />
					<ArrowLeftIcon className="w-6 h-6 text-textDark hidden md:block" />
				</Button>
			</div>

			{record ? (
				<BusinessDetailsForm
					key={record.id}
					states={states}
					initialValues={recordToFormValues(record)}
					submitLabel="Save business details"
					submitStatus={mutation.status}
					onSubmitForm={(payload) =>
						mutation.mutate({
							locationId: location_id,
							guestId: user.id,
							businessDetailId: record.id,
							payload,
						})
					}
				/>
			) : null}
		</SettingsContainer>
	);
};

export default BusinessDetailsEditPage;
